/**
 * Modal table comparing recommended Solar EPC / purchase by partner (GoodLeap, IGS, LightReach)
 * across all visible dashboard AI design cards.
 */
import React, { useCallback, useEffect, useMemo } from 'react';
import { Modal, Spin, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Info } from '@phosphor-icons/react';
import { isLayoutProductionOverAnnualUsage } from '../utils/layout';
import { EPC_DISPLAY_PARTNERS, type DesignEpcCell, type EpcDisplayPartner } from '../utils/finance';
import { OBD_AI_DESIGNS_EPC_MODAL_Z } from '../oneButtonOverlayZ';
import type { DashboardAiDesignEpcRow, DashboardAiDesignsEpcModalProps } from './DashboardAiDesignsEpcModal.types';

type EpcTableRow = DashboardAiDesignEpcRow & { key: string };

function isRowLevelPricingFailure(detail: string): boolean {
    const d = detail.trim();
    return /^(Pricing failed|Missing system size|Cannot compute pricing)/i.test(d);
}

function renderUnavailableEpc(detail: string): React.ReactNode {
    const msg = detail.trim() || 'Pricing unavailable for this partner';
    return (
        <span className="obd-ai-designs-epc-modal__na-wrap">
            <span className="obd-ai-designs-epc-modal__na">N/A</span>
            <Tooltip title={msg} mouseEnterDelay={0.15}>
                <span
                    className="obd-ai-designs-epc-modal__na-info"
                    role="img"
                    aria-label="Why pricing is unavailable"
                >
                    <Info size={12} weight="bold" aria-hidden />
                </span>
            </Tooltip>
        </span>
    );
}

function formatEpcSystemKw(systemKw: string): string {
    const n = Number(systemKw);
    if (!Number.isFinite(n) || n <= 0) return '—';
    return `${n.toFixed(2)} kW`;
}

function formatEpcProduction(estProd: number | undefined): string {
    if (estProd == null || !Number.isFinite(estProd) || estProd <= 0) return '—';
    return `${Math.round(estProd).toLocaleString()} kWh/yr`;
}

function formatEpcMultiplier(multiplier: number | null | undefined): string {
    if (multiplier == null || !Number.isFinite(multiplier)) return '—';
    return `×${Number(multiplier).toFixed(3)}`;
}

function renderEpcCell(
    row: EpcTableRow,
    partner: EpcDisplayPartner,
    getEpcByPartner: DashboardAiDesignsEpcModalProps['getEpcByPartner'],
    getPartnerCellState: DashboardAiDesignsEpcModalProps['getPartnerCellState'],
): React.ReactNode {
    const state = getPartnerCellState(row.cardKey, partner, row.estProd, row.systemKw);
    if (state?.status === 'loading') {
        return <Spin size="small" />;
    }
    if (state?.status === 'error') {
        const detail = state.errorDetail || '';
        if (isRowLevelPricingFailure(detail)) {
            return (
                <Tooltip title={detail}>
                    <span className="obd-ai-designs-epc-modal__error" role="img" aria-label="Pricing error">
                        !
                    </span>
                </Tooltip>
            );
        }
        return renderUnavailableEpc(detail);
    }
    const epc: DesignEpcCell = getEpcByPartner(row.cardKey, row.estProd, row.systemKw)[partner];
    if (epc.status === 'ok') {
        return <span className="obd-ai-designs-epc-modal__value">{epc.formatted}</span>;
    }
    if (state?.status === 'ok') {
        const detail = state.errorDetail?.trim();
        if (detail) return renderUnavailableEpc(detail);
        return <span className="obd-ai-designs-epc-modal__na">N/A</span>;
    }
    return <span className="obd-ai-designs-epc-modal__muted">—</span>;
}

export function DashboardAiDesignsEpcModal({
    theme,
    open,
    onClose,
    designs,
    ensurePricingForCard,
    getEpcByPartner,
    getPartnerCellState,
    selectedCardKey,
    annualUsageCapKwh,
    zIndex = OBD_AI_DESIGNS_EPC_MODAL_Z,
}: DashboardAiDesignsEpcModalProps) {
    const sellableDesigns = useMemo(
        () =>
            annualUsageCapKwh != null
                ? designs.filter((d) => !isLayoutProductionOverAnnualUsage(d.estProd, annualUsageCapKwh))
                : designs,
        [designs, annualUsageCapKwh],
    );

    const tableRows = useMemo<EpcTableRow[]>(
        () => sellableDesigns.map((d) => ({ ...d, key: d.cardKey })),
        [sellableDesigns],
    );

    const selectedRow = useMemo(
        () => (selectedCardKey ? tableRows.find((row) => row.cardKey === selectedCardKey) : undefined),
        [selectedCardKey, tableRows],
    );

    const loadAllDesignPricing = useCallback(() => {
        for (const design of sellableDesigns) {
            void ensurePricingForCard(design.cardKey, design.estProd, design.systemKw);
        }
    }, [sellableDesigns, ensurePricingForCard]);

    useEffect(() => {
        if (!open) return;
        loadAllDesignPricing();
    }, [open, loadAllDesignPricing]);

    const columns = useMemo<ColumnsType<EpcTableRow>>(() => {
        const partnerColumns: ColumnsType<EpcTableRow> = EPC_DISPLAY_PARTNERS.map((partner) => ({
            title: partner,
            dataIndex: partner,
            key: partner,
            align: 'right' as const,
            width: partner === 'LightReach' ? 120 : 108,
            render: (_value, row) => renderEpcCell(row, partner, getEpcByPartner, getPartnerCellState),
        }));

        return [
            {
                title: 'Design',
                dataIndex: 'label',
                key: 'label',
                fixed: 'left' as const,
                width: 200,
                render: (_value, row) => (
                    <div className="obd-ai-designs-epc-modal__design-cell">
                        {row.snapshotUrl ? (
                            <img
                                src={row.snapshotUrl}
                                alt=""
                                className="obd-ai-designs-epc-modal__thumb"
                            />
                        ) : (
                            <span className="obd-ai-designs-epc-modal__thumb obd-ai-designs-epc-modal__thumb--empty" aria-hidden />
                        )}
                        <span className="obd-ai-designs-epc-modal__design-label">{row.label}</span>
                    </div>
                ),
            },
            {
                title: 'System',
                dataIndex: 'systemKw',
                key: 'systemKw',
                align: 'right' as const,
                width: 88,
                render: (_value, row) => (
                    <span className="obd-ai-designs-epc-modal__metric">{formatEpcSystemKw(row.systemKw)}</span>
                ),
            },
            {
                title: 'Production',
                dataIndex: 'estProd',
                key: 'estProd',
                align: 'right' as const,
                width: 112,
                render: (_value, row) => (
                    <span className="obd-ai-designs-epc-modal__metric">{formatEpcProduction(row.estProd)}</span>
                ),
            },
            {
                title: 'Multiplier',
                dataIndex: 'multiplier',
                key: 'multiplier',
                align: 'right' as const,
                width: 96,
                render: (_value, row) => (
                    <span className="obd-ai-designs-epc-modal__metric obd-ai-designs-epc-modal__metric--mono">
                        {formatEpcMultiplier(row.multiplier)}
                    </span>
                ),
            },
            ...partnerColumns,
        ];
    }, [getEpcByPartner, getPartnerCellState]);

    const handleCancel = useCallback(() => {
        onClose();
    }, [onClose]);

    return (
        <Modal
            open={open}
            onCancel={handleCancel}
            footer={null}
            centered
            destroyOnHidden
            width="min(96vw, 1040px)"
            zIndex={zIndex}
            title="Design EPC by partner"
            rootClassName={`obd-ai-designs-epc-modal obd-theme-${theme}`}
            classNames={{ body: 'obd-ai-designs-epc-modal__body' }}
        >
            <p className="obd-ai-designs-epc-modal__hint">
                Recommended Solar offer EPC or purchase price per finance partner. Values load from the pricing API.
            </p>
            {selectedRow ? (
                <div className="obd-ai-designs-epc-modal__selected-summary" role="status">
                    <span className="obd-ai-designs-epc-modal__selected-badge">Selected</span>
                    <span className="obd-ai-designs-epc-modal__selected-name">{selectedRow.label}</span>
                    <span className="obd-ai-designs-epc-modal__selected-stat">
                        <span className="obd-ai-designs-epc-modal__selected-stat-label">System</span>
                        {formatEpcSystemKw(selectedRow.systemKw)}
                    </span>
                    <span className="obd-ai-designs-epc-modal__selected-stat">
                        <span className="obd-ai-designs-epc-modal__selected-stat-label">Production</span>
                        {formatEpcProduction(selectedRow.estProd)}
                    </span>
                    <span className="obd-ai-designs-epc-modal__selected-stat">
                        <span className="obd-ai-designs-epc-modal__selected-stat-label">Multiplier</span>
                        {formatEpcMultiplier(selectedRow.multiplier)}
                    </span>
                </div>
            ) : null}
            <Table<EpcTableRow>
                className="obd-ai-designs-epc-modal__table"
                columns={columns}
                dataSource={tableRows}
                pagination={false}
                size="small"
                rowClassName={(row) =>
                    selectedCardKey && row.cardKey === selectedCardKey
                        ? 'obd-ai-designs-epc-modal__row--selected'
                        : ''
                }
                scroll={{ x: 920, y: Math.min(480, Math.max(200, tableRows.length * 52)) }}
                locale={{ emptyText: 'No designs to compare' }}
            />
        </Modal>
    );
}
